import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchAllocationStatusIfNeeded,
  fetchQuarterType,
  fetchReturnType,
  fetchTANByEntityIfNeeded,
} from '../../../../store/actions/entityTanActions.js'
import {
  resetMonth,
  resetSelections,
  setSelectedAllocateFY,
  setSelectedAllocateStatus,
  setSelectedEntity,
  setSelectedForm,
  setSelectedMonth,
  setSelectedQuarter,
  setSelectedReturnType,
  setSelectedStatus,
  setSelectedTAN,
  setSelectedTANs,
  setSelectedYear,
} from '../../../../store/slice/entityTanSlice.js'
import { ADMIN } from '../../../../utils/role-capabilities.js'
import CustomButton from '../../CustomButton'
import CustomMultiSelect from '../../CustomMultiSelect'
import CustomSelect from '../../CustomSelect'
import FilterSection from '../../FilterSection'

const KeyFilter = ({
  handleViewClick,
  setKeyFilterData = () => { },
  isYear = false,
  isMonth = false,
  isStatus = false,
  isAllocateStatus = false,
  isQuarter = false,
  isReturnType = false,
  isForm = false,
  isFormView = 0,
  isMultiTAN = false,
  setIsFormView = () => {},
  btnName = 'View',
  setActiveTabIndex = () => { },
  defaultSearchCOList,
}) => {
  const dispatch = useDispatch()
  const {
    entityList,
    tanListByEntity,
    allocationStatusList,
    quarterTypeList,
    returnTypeList,
    selectedEntityUuid,
    selectedTANUuid,
    selectedTANUuids,
    selectedYear,
    selectedMonth,
    selectStatus,
    selectAllocateStatus,
    selectQuarter,
    selectReturnType,
    selectForm,
    tanLoading,
    tanError,
    selectedAllocateFY,
  } = useSelector(state => state.entityTan)

  const userRole = useSelector(state => state.user.role)
  const isRoleDisabled = userRole === ADMIN

  const [clicked, setClicked] = useState(false)

  const yearOptions = useMemo(() => {
    const currentYear = dayjs().year()
    return Array.from({ length: 7 }, (_, i) => {
      const start = currentYear - i
      const end = (start + 1).toString().slice(-2)
      return { label: `${start}-${end}`, value: `${start}-${end}` }
    })
  }, [])

  const getMonthsInFY = fy => {
    if (!fy) return []
    const [startYear, endYearShort] = fy.split('-')
    const endYear = Number.parseInt('20' + endYearShort, 10)
    const start = dayjs(`${startYear}-04-01`)
    const end = dayjs(`${endYear}-03-31`)

    const months = []
    let iter = start
    while (iter.isBefore(end) || iter.isSame(end, 'month')) {
      months.push({
        label: iter.format('MMM YYYY'),
        value: { year: iter.year(), month: iter.month() + 1 },
      })
      iter = iter.add(1, 'month')
    }
    return months
  }

  const monthOptions = useMemo(
    () => getMonthsInFY(selectedYear),
    [selectedYear]
  )

  // Map entities
  const entityOptions = useMemo(() => {
    if (!entityList?.content) return []
    return entityList.content.map(item => ({
      label: item.entityName,
      value: item.uuid,
    }))
  }, [entityList])

  useEffect(() => {
    if (!selectedEntityUuid) return
    dispatch(fetchTANByEntityIfNeeded(selectedEntityUuid))
  }, [dispatch, selectedEntityUuid])

  useEffect(() => {
    if (!selectedAllocateFY) return
    dispatch(fetchAllocationStatusIfNeeded())
  }, [dispatch, selectedAllocateFY])

  useEffect(() => {
    if (!selectedYear) return
    dispatch(fetchQuarterType())
  }, [dispatch, selectedYear])

  useEffect(() => {
    if (!selectQuarter) return
    dispatch(fetchReturnType())
  }, [dispatch, selectQuarter])

  const tanOptionsRedux = useMemo(
    () =>
      tanListByEntity[selectedEntityUuid]?.map(t => ({
        label: t.taxDeductionAccountNumber,
        value: t.uuid,
      })) || [],
    [tanListByEntity, selectedEntityUuid]
  )

  // -------------------
  // Handle dropdown changes
  // -------------------
  const handleEntityChange = e => {
    const uuid = e.target.value
    dispatch(setSelectedEntity(uuid))
    dispatch(setSelectedTAN(''))
    dispatch(setSelectedTANs([]))
    setSelectedMonth([])
    setSelectedYear('')
    setKeyFilterData([])
    setClicked(false)
  }

  const handleTANChange = e => {
    dispatch(setSelectedTAN(e.target.value))
    setKeyFilterData([])
    setClicked(false)
  }

  const handleMultiTANChange = e => {
    dispatch(setSelectedTANs(e.target.value))
    setKeyFilterData([])
    setClicked(false)
  }

  // -------------------
  // Handle View click
  // -------------------
  const handleClick = () => {
    const entity = entityOptions.find(e => e.value === selectedEntityUuid)

    let tanValue, tanLabel
    if (isMultiTAN) {
      const selectedTans = tanOptionsRedux.filter(t =>
        selectedTANUuids.includes(t.value)
      )
      tanValue = selectedTans.map(t => t.value).join(',')
      tanLabel = selectedTans.map(t => t.label).join(',')
    } else {
      const tan = tanOptionsRedux.find(t => t.value === selectedTANUuid)
      tanValue = tan?.value || null
      tanLabel = tan?.label || null
    }

    handleViewClick({
      entityName: entity?.label || null,
      entityUuid: selectedEntityUuid || null,
      tanMasterUuid: tanValue,
      tan: tanLabel,
      year: selectedYear || null,
      allocateFy: selectedAllocateFY || null,
      status: selectStatus || null,
      allocationStatus: selectAllocateStatus || null,
      periodFilters: selectedMonth.length ? selectedMonth : null,
      quarter: selectQuarter,
      returnType: selectReturnType,
      form: selectForm,
    })

    setClicked(true)
  }

  // Status options
  const statusOptions = useMemo(
    () => [
      { label: 'All', value: 'All' },
      { label: 'Completed', value: 'Completed' },
      { label: 'Pending', value: 'Pending' },
    ],
    []
  )

  const formOptions = useMemo(() => {
    if (!selectedYear) return []
    const fyStartYear = Number.parseInt(selectedYear.split('-')[0], 10)
    // FY 2026-27 onwards uses 140/144, before that uses 26Q/27Q
    if (fyStartYear >= 2026) {
      return [
        { label: '140', value: '140' },
        { label: '144', value: '144' },
      ]
    }
    return [
      { label: '26Q', value: '26Q' },
      { label: '27Q', value: '27Q' },
    ]
  }, [selectedYear])

  const rules = [
    { cond: true, invalid: !selectedEntityUuid },
    { cond: !isMultiTAN, invalid: !selectedTANUuid },
    { cond: isMultiTAN, invalid: !selectedTANUuids.length },
    { cond: isYear, invalid: !selectedYear },
    { cond: isMonth, invalid: !selectedMonth?.length },
    { cond: isStatus, invalid: !selectStatus },
    { cond: isAllocateStatus, invalid: !selectAllocateStatus },
    { cond: isQuarter, invalid: !selectQuarter },
    { cond: isReturnType, invalid: !selectReturnType },
    { cond: isForm, invalid: !selectForm },
  ]

  const isButtonDisabled = rules.some(rule => rule.cond && rule.invalid)

  const titleFilter = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {isFormView ? (
        <Tooltip>
          <IconButton
            size="small"
            sx={{
              color: '#ff6b35',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            onClick={e => {
              e.stopPropagation()
              setIsFormView(0)
              setActiveTabIndex(0)
              dispatch(resetSelections())
            }}
          >
            <KeyboardArrowLeftIcon />
            Go Back
          </IconButton>
        </Tooltip>
      ) : null}

      <Typography
        component="span"
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          color: '#333',
        }}
      >
        Filter
      </Typography>
    </Box>
  )

  const getTanPlaceholder = () => {
    if (!selectedEntityUuid) return 'Select Entity TAN'
    if (tanLoading) return 'Loading TAN...'
    if (tanError) return 'Failed to load TAN'
    if (tanOptionsRedux.length === 0) return 'No TAN Found!'
    return 'Select Entity TAN'
  }

  const tanPlaceholder = getTanPlaceholder()

  useEffect(() => {
    if (defaultSearchCOList?.entityName && entityOptions.length) {
      const matchedEntity = entityOptions.find(
        opt => opt.label === defaultSearchCOList.entityName
      )

      if (matchedEntity) {
        dispatch(setSelectedEntity(matchedEntity.value))
      }
    }
  }, [defaultSearchCOList, entityOptions])

  useEffect(() => {
    if (defaultSearchCOList?.entityTan && tanOptionsRedux.length) {
      if (isMultiTAN) {
        const tanLabels = new Set(defaultSearchCOList.entityTan.split(','))
        const matchedUuids = tanOptionsRedux
          .filter(opt => tanLabels.has(opt.label))
          .map(opt => opt.value)
        if (matchedUuids.length) {
          dispatch(setSelectedTANs(matchedUuids))
        }
      } else {
        const matchedTAN = tanOptionsRedux.find(
          opt => opt.label === defaultSearchCOList.entityTan
        )
        if (matchedTAN) {
          dispatch(setSelectedTAN(matchedTAN.value))
        }
      }
    }
  }, [defaultSearchCOList, tanOptionsRedux])

  useEffect(() => {
    if (defaultSearchCOList?.quarter) {
      dispatch(setSelectedQuarter(defaultSearchCOList.quarter))
    }
  }, [defaultSearchCOList])

  useEffect(() => {
    if (defaultSearchCOList?.returnType) {
      dispatch(setSelectedReturnType(defaultSearchCOList.returnType))
    }
  }, [defaultSearchCOList])

  useEffect(() => {
    if (defaultSearchCOList?.formType && formOptions.length) {
      const matchedForm = formOptions.find(
        opt => opt.value === defaultSearchCOList.formType
      )

      if (matchedForm) {
        dispatch(setSelectedForm(matchedForm.value))
      }
    }
  }, [defaultSearchCOList, formOptions])

  useEffect(() => {
    if (defaultSearchCOList?.financialYear && yearOptions.length) {
      const matchedYear = yearOptions.find(
        opt => opt.value === defaultSearchCOList.financialYear
      )

      if (matchedYear) {
        dispatch(setSelectedYear(matchedYear.value))
      }
    }
  }, [defaultSearchCOList, yearOptions])

  return (
    <FilterSection title={titleFilter}>
      {/* Entity Name */}
      <CustomSelect
        // label="Entity Name"
        value={selectedEntityUuid || ''}
        onChange={handleEntityChange}
        options={entityOptions}
        getOptionLabel={option => option.label}
        getOptionValue={option => option.value}
        placeholder={'Select Entity Name'}
        disabled={isFormView || isRoleDisabled}
      />

      {/* Entity TAN */}

      {isMultiTAN ? (
        <CustomMultiSelect
          // label="Entity TAN"
          value={selectedTANUuids || []}
          onChange={handleMultiTANChange}
          options={tanOptionsRedux}
          getOptionLabel={option => option.label}
          getOptionValue={option => option.value}
          placeholder={tanPlaceholder}
          disabled={
            !selectedEntityUuid ||
            !tanOptionsRedux.length ||
            isFormView ||
            isRoleDisabled
          }
        />
      ) : (
        <CustomSelect
          // label="Entity TAN"
          value={selectedTANUuid || ''}
          onChange={handleTANChange}
          options={tanOptionsRedux}
          getOptionLabel={option => option.label}
          getOptionValue={option => option.value}
          placeholder={tanPlaceholder}
          disabled={
            !selectedEntityUuid ||
            !tanOptionsRedux.length ||
            isFormView ||
            isRoleDisabled
          }
        />
      )}

      {isYear && (
        <CustomSelect
          // label="Tax Year"
          value={selectedYear || ''}
          onChange={e => {
            dispatch(setSelectedYear(e.target.value))
            dispatch(setSelectedForm(''))
            dispatch(resetMonth())
            setSelectedMonth([])
            setKeyFilterData([])
            setClicked(false)
          }}
          options={yearOptions}
          getOptionLabel={option => option.label}
          getOptionValue={option => option.value}
          placeholder="Select Tax Year"
          disabled={
            (isMultiTAN ? !selectedTANUuids.length : !selectedTANUuid) ||
            isFormView
          }
        />
      )}

      {isMonth && (
        <>
          {/* Month multi-select */}
          <CustomMultiSelect
            // label="Month"
            value={selectedMonth.map(m => JSON.stringify(m))} // store as string for UI
            onChange={e => {
              // convert back to array of {year, month}
              const selected = e.target.value.map(v => JSON.parse(v))
              dispatch(setSelectedMonth(selected))
              setClicked(false)
            }}
            options={monthOptions.map(opt => ({
              ...opt,
              value: JSON.stringify(opt.value), // stringify for stable comparison
            }))}
            getOptionLabel={option => option.label}
            getOptionValue={option => option.value}
            placeholder="Select Month"
            disabled={!selectedYear || isRoleDisabled}
          />
        </>
      )}

      {/* Year & Status Dropdowns */}
      {isStatus && (
        <>
          <CustomSelect
            // label="Status"
            value={selectStatus || ''}
            onChange={e => {
              dispatch(setSelectedStatus(e.target.value))
              setKeyFilterData([])
              setClicked(false)
            }}
            options={statusOptions}
            getOptionLabel={option => option.label}
            getOptionValue={option => option.value}
            placeholder="Select Status"
            disabled={!selectedYear || isRoleDisabled}
          />
        </>
      )}

      {isAllocateStatus && (
        <>
          <CustomMultiSelect
            // label="Tax Year"
            value={selectedAllocateFY?.map(m => JSON.stringify(m))}
            onChange={e => {
              const selected = e.target.value.map(v => JSON.parse(v))

              dispatch(setSelectedAllocateFY(selected))
              dispatch(resetMonth())
              setSelectedMonth([])
              setKeyFilterData([])
              setClicked(false)
            }}
            options={yearOptions.map(opt => ({
              ...opt,
              value: JSON.stringify(opt.value), // stringify for stable comparison
            }))}
            getOptionLabel={option => option.label}
            getOptionValue={option => option.value}
            placeholder="Select Tax Year"
            disabled={isMultiTAN ? !selectedTANUuids.length : !selectedTANUuid}
          />
          <CustomMultiSelect
            // label="Allocation Status"
            value={selectAllocateStatus || ''}
            onChange={e => {
              dispatch(setSelectedAllocateStatus(e.target.value))
              setKeyFilterData([])
              setClicked(false)
            }}
            options={allocationStatusList}
            getOptionLabel={option => option}
            getOptionValue={option => option}
            placeholder="Select Allocation Status"
            disabled={selectedAllocateFY.length == 0 || isRoleDisabled}
          />
        </>
      )}

      {isQuarter && (
        <>
          <CustomSelect
            // label="Quarter"
            value={selectQuarter || ''}
            onChange={e => {
              dispatch(setSelectedQuarter(e.target.value))
              setKeyFilterData([])
              setClicked(false)
            }}
            options={quarterTypeList}
            getOptionLabel={option => option}
            getOptionValue={option => option}
            placeholder="Select Quarter"
            disabled={!selectedYear || isFormView}
          />
        </>
      )}

      {isReturnType && (
        <>
          <CustomSelect
            // label="Return Type"
            value={selectReturnType || ''}
            onChange={e => {
              dispatch(setSelectedReturnType(e.target.value))
              setKeyFilterData([])
              setClicked(false)
            }}
            options={returnTypeList}
            getOptionLabel={option => option}
            getOptionValue={option => option}
            placeholder="Select Return Type"
            disabled={!selectQuarter || isFormView}
          />
        </>
      )}

      {isForm && (
        <>
          <CustomSelect
            // label="Form"
            value={selectForm || ''}
            onChange={e => {
              dispatch(setSelectedForm(e.target.value))
              setKeyFilterData([])
              setClicked(false)
            }}
            options={formOptions}
            getOptionLabel={option => option.label}
            getOptionValue={option => option.value}
            placeholder="Select Form"
            disabled={!selectReturnType || isFormView}
          />
        </>
      )}

      {/* View Button */}

      <CustomButton
        onClick={clicked ? () => {} : handleClick}
        disabled={isButtonDisabled || isFormView}
      >
        {btnName}
      </CustomButton>
    </FilterSection>
  )
}

KeyFilter.propTypes = {
  handleViewClick: PropTypes.func.isRequired,
  setKeyFilterData: PropTypes.func,
  isYear: PropTypes.bool,
  isMonth: PropTypes.bool,
  isStatus: PropTypes.bool,
  isAllocateStatus: PropTypes.bool,
  isQuarter: PropTypes.bool,
  isReturnType: PropTypes.bool,
  isForm: PropTypes.bool,
  isFormView: PropTypes.number,
  isMultiTAN: PropTypes.bool,
  setIsFormView: PropTypes.func,
  btnName: PropTypes.string,
  setActiveTabIndex: PropTypes.func,
  defaultSearchCOList: PropTypes.shape({
    entityName: PropTypes.string,
    entityTan: PropTypes.string,
    quarter: PropTypes.string,
    returnType: PropTypes.string,
    formType: PropTypes.string,
    financialYear: PropTypes.string,
  }),
}

export default KeyFilter
